import { isValidJSONStr, nthIndex } from "@utils/string-helpers";

// given a array of keys, return the nested value
// ({hi: {there: {my: "friend"}}}, ["hi", "there"]) => {my: "friend"}
export function useKeyArrToGetNestedObject(obj, keyArr) {
  try {
    return keyArr.reduce((accumulator, el) => {
      return accumulator[el];
    }, obj);
  } catch (error) {
    return null;
  }
}

// return true if x is an object
export function isPlainObject(x) {
  return Object(x) === x && !Array.isArray(x);
}

export function isEmptyObject(obj) {
  return Object.entries(obj).length === 0 && obj.constructor === Object;
}

// return an array of strings that are paths to the final keys
// when x is a object with nested objects

// x = {hi: {there: {my: {friend: 'or foe'}}, how: {are: {we: 'now', you: 'now'}}}}
// returns
// ['hi.there.my.friend', 'hi.how.are.we', 'hi.how.are.you']
export function stringifyNestedKeyPaths(x, pre = [], acc = []) {
  return Object.keys(x).reduce(
    (acc, k) =>
      isPlainObject(x[k])
        ? acc.concat(stringifyNestedKeyPaths(x[k], pre.concat(k), acc))
        : acc.concat([pre.concat(k).join(".")]),
    []
  );
}

/**
 * Returns an array of strings that are paths to the final keys
 * when object is nested with objects or arrays
 * @param {Object} obj - Object with nested objects/arrays
 * @param {string} prefix - stringified path
 */
export function stringifyNestedObjectsAndArrays(obj, prefix = "") {
  return Object.keys(obj).reduce((acc, k) => {
    const currentPath = prefix ? `${prefix}.${k}` : k;
    if (isPlainObject(obj[k]) && obj[k] !== null) {
      return acc.concat(stringifyNestedObjectsAndArrays(obj[k], currentPath));
    } else if (Array.isArray(obj[k]) && obj[k].length > 0) {
      obj[k].forEach((key, i) => {
        acc = acc.concat(
          isPlainObject(key)
            ? stringifyNestedObjectsAndArrays(key, `${currentPath}.${i}`)
            : `${currentPath}.${i}`
        );
      });
      return acc;
    } else {
      return acc.concat(currentPath);
    }
  }, []);
}

// a recursive function to create a nested object from a keyPath array
// (caution: this function cuts off the last key)

// ['1', '2', '3'], {hi: 'there'}, true
// returns
//  {
//    1: {
//      2: {
//        hi: 'there'
//      }
//    }
//  }
export function constructNestedObjectUsingKeyArr(
  shrinkingKeyArr,
  objValue,
  isFirstPass
) {
  // on the last pass, when the shrinkingKeyArr is empty...
  if (shrinkingKeyArr.length === 0) {
    // return the created object
    return objValue;
  } else {
    // on the first pass
    if (isFirstPass) {
      // return the initial and deepest object
      // pass a shorter keyPath and the initial and deepest object to the next iteration
      return constructNestedObjectUsingKeyArr(
        shrinkingKeyArr.slice(0, -1),
        objValue
      );

      // on subsequent passes,
      // return a shorter keyPath
      // and a new object with the last element in the keyPathArr as the key
      // and the last iteration's object as the value
    } else {
      return constructNestedObjectUsingKeyArr(shrinkingKeyArr.slice(0, -1), {
        [shrinkingKeyArr.slice(-1)]: objValue,
      });
    }
  }
}

// recursively traverses a JSON object, drills down via iterProp; iterProp is expected to be an key prop that will map to an arr of objs
// keyValue is a string property value, storedValue will copy only the values specified as a string or array of string keys
// innerMost property is true, will only add storedValue keys in the inner most object
export function traverseJSON(
  jsonObj,
  iterProp,
  keyValue,
  storedValueKeys,
  innerMost = false,
  accum = {}
) {
  let storedValueObj;
  const generateStoredValueObject = (keyPaths, objToReduce) =>
    keyPaths &&
    (Array.isArray(keyPaths)
      ? keyPaths.reduce((acc, cur) => {
          acc[cur] = objToReduce[cur];
          return acc;
        }, {})
      : { [keyPaths]: objToReduce[keyPaths] });
  let ind = jsonObj.hasOwnProperty(iterProp)
    ? jsonObj[iterProp].length - 1
    : -1;
  if (jsonObj[iterProp].length === 0) {
    storedValueObj = generateStoredValueObject(storedValueKeys, jsonObj);
    accum[jsonObj[keyValue]] = storedValueObj;
  }
  while (ind > -1) {
    let currentObj = jsonObj[iterProp][ind];
    storedValueObj = generateStoredValueObject(storedValueKeys, currentObj);
    if (!accum.hasOwnProperty(currentObj[keyValue])) {
      accum[currentObj[keyValue]] = {
        // [keyValue]: currentObj[keyValue],
        ...(innerMost ? {} : storedValueObj),
        ...(currentObj[iterProp].length > 0 ? {} : storedValueObj),
      };
      let subInd = currentObj[iterProp].length - 1;
      while (subInd > -1) {
        accum[currentObj[keyValue]] = traverseJSON(
          currentObj[iterProp][subInd],
          iterProp,
          keyValue,
          storedValueKeys,
          innerMost,
          accum[currentObj[keyValue]]
        );
        subInd--;
      }
    }
    ind--;
  }
  return accum;
}

// returns array of keys in order of path
export function findKeyPathByValue(obj, value) {
  for (var key in obj) {
    if (obj[key] && typeof obj[key] === "object") {
      let result = findKeyPathByValue(obj[key], value);
      if (result) {
        result.unshift(key);
        return result;
      }
    } else if (obj[key] === value) {
      return [key];
    }
  }
}

export function convertObjectToOptionArr(
  flatObj,
  objValKey = "value",
  objLabelKey = "label",
  sameLabelKey = false
) {
  return Object.keys(flatObj).map((keyVal) => ({
    [objValKey]: keyVal,
    [objLabelKey]: sameLabelKey ? keyValue : flatObj[keyVal],
  }));
}

export function renderJSONValue(value) {
  try {
    let jsonView = typeof value === "string" ? value : JSON.stringify(value);
    return isValidJSONStr(jsonView) ? JSON.parse(jsonView) : null;
  } catch (err) {
    console.warn(err);
    return null;
  }
}

export function setSchemaDefaults(
  objToIter,
  customDefault,
  definitions,
  overrideDefault = false
) {
  return Object.keys(objToIter).reduce((acc, cur) => {
    if (objToIter[cur].hasOwnProperty("properties")) {
      acc[cur] = setSchemaDefaults(
        objToIter[cur].properties,
        customDefault,
        definitions,
        overrideDefault
      );
    } else if (objToIter[cur].hasOwnProperty("allOf")) {
      acc[cur] = setSchemaDefaults(
        objToIter[cur].allOf,
        customDefault,
        definitions,
        overrideDefault
      );
    } else if (objToIter[cur].hasOwnProperty("default")) {
      let currentObj = objToIter[cur].default;
      if (typeof currentObj === "object" && overrideDefault) {
        for (let i in currentObj) {
          currentObj[i] = overrideDefault;
        }
      } else if (typeof currentObj === "string" && overrideDefault) {
        currentObj = overrideDefault;
      }
      acc[cur] = currentObj;
    } else if (objToIter[cur].hasOwnProperty("$ref") && definitions) {
      let def = objToIter[cur].$ref.split("/");
      def = def[def.length - 1];
      acc[cur] = definitions[def].hasOwnProperty("properties")
        ? setSchemaDefaults(
            definitions[def].properties,
            customDefault,
            definitions,
            overrideDefault
          )
        : overrideDefault
        ? overrideDefault
        : customDefault;
    } else if (objToIter[cur].type === "array") {
      acc[cur] = [];
    } else {
      acc[cur] = customDefault;
    }
    return acc;
  }, {});
}

export const flattenPayload = (object) => {
  return Object.values(object).reduce((acc, cur) => ({ ...acc, ...cur }), {});
};

export const removeSetOfKeysFromObject = (originalObj, keys = []) => {
  if (typeof originalObj !== "object" || !Array.isArray(keys)) {
    throw new TypeError(
      "Invalid Type, arg originalObj must be an object and arg keys must be an array"
    );
  }

  let originalObjKeys = Object.keys(originalObj);
  if (originalObjKeys.length === 0 || keys.length === 0) {
    return originalObj;
  }

  let keysToRemove = {},
    updatedObj = {};
  keys.forEach((key) => (keysToRemove = { ...keysToRemove, [key]: true }));

  originalObjKeys.forEach((originalKey) => {
    updatedObj = {
      ...updatedObj,
      ...(!keysToRemove[originalKey]
        ? { [originalKey]: originalObj[originalKey] }
        : {}),
    };
  });

  return updatedObj;
};

export const flattenNestedObj = (
  data,
  response = {},
  prefix = "",
  onlyLastKey = false
) => {
  if (
    typeof data !== "object" ||
    !data ||
    typeof response !== "object" ||
    !response
  ) {
    throw new TypeError("Invalid input, param must be an object and not null.");
  } else if (prefix && typeof prefix !== "string") {
    throw new TypeError("Invalid input, param be a string.");
  } else {
    for (const [key, value] of Object.entries(data)) {
      let newFlatKey;
      if (!isNaN(parseInt(key)) && prefix.includes("[]")) {
        newFlatKey =
          (prefix.charAt(prefix.length - 1) == "."
            ? prefix.slice(0, -1)
            : prefix) + `[${key}]`;
      } else if (!prefix.includes(".") && prefix.length) {
        newFlatKey = `${prefix}.${key}`;
      } else {
        newFlatKey = `${prefix}${key}`;
      }
      if (
        typeof value === "object" &&
        value !== null &&
        Object.keys(value).length
      ) {
        flattenNestedObj(value, response, `${newFlatKey}.`, onlyLastKey);
      } else {
        if (onlyLastKey) {
          newFlatKey = newFlatKey.split(".").pop();
        }
        if (Array.isArray(response)) {
          response.push({ [newFlatKey.replace("[]", "")]: value });
        } else {
          response[newFlatKey.replace("[]", "")] = value;
        }
      }
    }
  }
  return response;
};

export const returnPathTreeList = (pathStr, includeLastValue = false) => {
  if (typeof pathStr !== "string") {
    throw new TypeError("Invalid input, param must be a string.");
  }
  let pathList = [];
  pathStr = pathStr.replace(/\.{2,}/g, ".").replace(/^\.|\.+$/, "");
  let subMatched = pathStr.match(/\./g) || [];
  for (let i = 1; i <= subMatched.length; i++) {
    pathList.push(pathStr.substring(0, nthIndex(pathStr, ".", i)));
  }
  return includeLastValue ? [...pathList, pathStr] : pathList;
};

export const allValuesCheck = (
  obj,
  fullMatch,
  boolToEval = true,
  strictBool
) => {
  const valueList = Object.values(obj);
  return fullMatch
    ? valueList.every((val) =>
        strictBool ? val === boolToEval : boolToEval ? val : !val
      )
    : valueList.some((val) =>
        strictBool ? val === boolToEval : boolToEval ? val : !val
      );
};

export const removeEmptyValues = (obj) => {
  const noEmptyValueObj = {};
  Object.keys(obj).forEach((key) => {
    if (typeof obj[key] === "object" && obj[key] !== null) {
      let newValue = removeEmptyValues(obj[key]);
      if (!isEmptyObject(newValue)) noEmptyValueObj[key] = newValue;
    } else if (
      typeof obj[key] !== "object" &&
      obj[key] !== "" &&
      obj[key] !== false
    ) {
      noEmptyValueObj[key] = obj[key];
    }
  });
  return noEmptyValueObj;
};
